// Floating reactions (README "Together"): a heart, note, star or moon floats up on the screen of everyone watching the
// same city. One dropped by a tap on the scene rises from that scene pixel on every page; one sent from the popup has
// no spot, and each page lets it rise from the bottom right of what it shows. Drawn in the page's overlay buffer:
// never in a scene, so PNG, MP4 and the mini player never show them. Poses are pure (tests/together.test.mjs).
import { REACTIONS, REACT_CAP, REACT_EVERY_MS } from './live.js';
import { W, H, TAU, clamp, smooth } from '../engine/core.js';

export const REACT_LIFE = 4;         // seconds on screen
export const REACT_MAX = 40;         // on screen at once: a burst pushes the oldest out
export const TAP_SLOP = 8;           // CSS px a press may wander and still be a tap: past it, it's a drag (site/js/app.js)
export const TAP_MS = 500;           // and how long it may last: longer is a press-and-hold, not a tap

/** The kind a tap on the scene drops: the one picked in the popup (stored as 'react-kind'), a heart for anything else. */
export const pickedKind = k => (REACTIONS.includes(k) ? k : 'heart');

/** Whether a press that ended (dx, dy) CSS px from where it started, `ms` later, was a tap. */
export const isTap = (dx, dy, ms) => Math.hypot(dx, dy) < TAP_SLOP && ms >= 0 && ms < TAP_MS;

/**
 * The scene pixel { x, y } under the CSS point (cx, cy), with `v` what the page shows (display.view: a phone's drag
 * included), or null off the scene: the ambient bars, a band's sky and ground, a cropped-off column.
 */
export function sceneSpot(v, cx, cy) {
  const u = (cx - v.left) / v.width, w = (cy - v.top) / v.height;
  if (!(u >= 0 && u < 1 && w >= 0 && w < 1)) return null;
  return { x: clamp(Math.floor(v.x + u * v.w), 0, W - 1), y: clamp(Math.floor(v.y + w * v.h), 0, H - 1) };
}

/** The spot a reaction from the worker carries ({ x, y }, whole scene pixels), or null for one without (or junk). */
export function spotOf(m) {
  if (!Number.isFinite(m?.x) || !Number.isFinite(m?.y)) return null;
  return { x: clamp(Math.round(m.x), 0, W - 1), y: clamp(Math.round(m.y), 0, H - 1) };
}

// 7×7 pixel art: X the colour, o its highlight. The Together popup's buttons use the same drawings (index.html).
export const SPRITES = {
  heart: ['.XX.XX.', 'XoXXXXX', 'XXXXXXX', 'XXXXXXX', '.XXXXX.', '..XXX..', '...X...'],
  note: ['...XXX.', '...X.XX', '...X..X', '...X...', '.XXX...', 'XoXX...', '.XX....'],
  star: ['...X...', '..XoX..', 'XXXXXXX', '.XXXXX.', '..XXX..', '.XX.XX.', '.X...X.'],
  moon: ['..XXX..', '.XoX...', 'XXX....', 'XXX....', 'XXX....', '.XXX...', '..XXX..'],
};
// the same colours as the popup's buttons (site/css/app.css, .tg-react)
export const COLOURS = {
  heart: ['#ff6b8b', '#ffc2cf'], note: ['#8fd3ff', '#e0f4ff'], star: ['#ffd95e', '#fff6c4'], moon: ['#fff0b3', '#ffffff'],
};
const OUTLINE = 'rgba(16, 10, 34, 0.55)';   // a dark rim keeps them readable over a bright sky or neon

/**
 * The worker's per-page bucket (take() in worker/social.js): `cap` at once, then one more for every full `every` ms.
 * take(now) spends one and says whether there was one, so the page drops what the worker would drop anyway.
 */
export function createBucket(cap = REACT_CAP, every = REACT_EVERY_MS) {
  let n = cap, at = null;
  return {
    take(now) {
      if (at !== null) {
        const from = Math.min(at, now), earned = Math.floor((now - from) / every);   // a clock that went back refills nothing
        n = Math.min(cap, n + earned);
        at = n === cap ? now : from + earned * every;
      }
      if (at === null) at = now;
      if (n < 1) return false;
      n--;
      return true;
    },
  };
}

/**
 * Adds a reaction of `kind` somewhere along `band` ({ x0, x1, y } in scene pixels: where it starts, above the
 * interface; x0 = x1 for a spot) at `now` (ms); `still` (prefers-reduced-motion) fades it in place. It rises 80-110 px,
 * less from near the top, so it never leaves the scene (a band would cut it off at its edge). Returns it, or null for
 * an unknown kind.
 */
export function spawnReaction(list, kind, { x0, x1, y, still = false }, now, rnd = Math.random) {
  if (!REACTIONS.includes(kind)) return null;
  const x = Math.round(x0 + rnd() * (x1 - x0)), y0 = Math.round(y), rise = Math.min(80 + Math.round(rnd() * 30), Math.max(0, y0 - 4));
  const r = { k: kind, x, y: y0, rise, ph: rnd(), born: now, still };
  list.push(r);
  if (list.length > REACT_MAX) list.splice(0, list.length - REACT_MAX);
  return r;
}

/** Where a reaction is `age` seconds in: { x, y, alpha } (its centre, whole pixels), or null before or after its life. */
export function reactionPose(r, age) {
  if (!(age >= 0) || age >= REACT_LIFE) return null;
  if (r.still) return { x: r.x, y: r.y, alpha: smooth(0, 0.4, age) * (1 - smooth(REACT_LIFE - 1.6, REACT_LIFE, age)) };
  const u = age / REACT_LIFE, up = 1 - (1 - u) ** 1.6;           // quick off the mark, then drifting up
  return {
    x: Math.round(r.x + Math.sin(TAU * (age / 1.7 + r.ph)) * (1 + 2 * u)),
    y: Math.round(r.y - r.rise * up),
    alpha: smooth(0, 0.15, age) * (1 - smooth(REACT_LIFE - 1.4, REACT_LIFE, age)),
  };
}

let art = null;                       // kind → 9×9 canvas (the sprite and its rim), drawn on first use
function sprite(kind) {
  const rows = SPRITES[kind], [main, light] = COLOURS[kind], s = rows.length + 2;
  const c = document.createElement('canvas');
  c.width = c.height = s;
  const x = c.getContext('2d'), on = (i, j) => rows[j]?.[i] !== undefined && rows[j][i] !== '.';
  x.fillStyle = OUTLINE;
  for (let j = -1; j <= rows.length; j++) for (let i = -1; i <= rows.length; i++) {
    if (on(i, j)) continue;
    let near = false;
    for (let dj = -1; dj <= 1 && !near; dj++) for (let di = -1; di <= 1; di++) if (on(i + di, j + dj)) { near = true; break; }
    if (near) x.fillRect(i + 1, j + 1, 1, 1);
  }
  rows.forEach((row, j) => [...row].forEach((ch, i) => {
    if (ch === '.') return;
    x.fillStyle = ch === 'o' ? light : main;
    x.fillRect(i + 1, j + 1, 1, 1);
  }));
  return c;
}

/** Draws the living reactions into `ctx` (scene pixels) and drops the finished ones. Returns how many it drew. */
export function drawReactions(ctx, list, now) {
  let drawn = 0, keep = 0;
  for (const r of list) {
    const age = (now - r.born) / 1000;
    if (age >= REACT_LIFE) continue;
    list[keep++] = r;
    const p = reactionPose(r, age);
    if (!p || p.alpha <= 0) continue;
    art ??= Object.fromEntries(REACTIONS.map(k => [k, sprite(k)]));
    ctx.globalAlpha = p.alpha;
    ctx.drawImage(art[r.k], p.x - 4, p.y - 4);
    drawn++;
  }
  list.length = keep;
  ctx.globalAlpha = 1;
  return drawn;
}
